import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BUCKETS,
  canComment,
  canWrite,
  freshNodeDisplay,
  freshPptxGeom,
  type AnalystRunSummary,
  type Bucket,
  type PresenceMember,
  type ProjectSummary,
} from '@integrate/contracts';
import {
  activeFilterCount,
  buildHierarchy,
  countsInHeadcount,
  describeNodeId,
  employeeIdFromNode,
  freshLens,
  personNodeId,
  selectionRollup,
  subtreePeopleIds,
  defaultAnalyticsTab,
  resolveAnalyticsTab,
} from '@integrate/core';
import { api } from './api.js';
import { useStore } from './store.js';
import { OrgChart, type OrgChartHandle } from './OrgChart.jsx';
import { SelectionPanel, bulkIntent } from './panels/SelectionPanel.jsx';
import { PhaseBar } from './PhaseBar.jsx';
import { phaseById } from './phases.js';
import { AssumptionsEditor } from './panels/AssumptionsEditor.jsx';
import { MembersEditor } from './panels/MembersEditor.jsx';
import { AccountModal } from './panels/AccountModal.jsx';
import { ProjectSettings } from './panels/ProjectSettings.jsx';
import { ScopeNotice } from './ui/ScopeNotice.jsx';
import { ErrorBoundary } from './ui/ErrorBoundary.js';
import { ThemeToggle } from './ui/ThemeToggle.js';
import { placeToUrl, readPlace, syncUrl, type ViewName } from './routing.js';
import { Workbench } from './ui/Workbench.js';
import { Section } from './panels/sections.js';
import { SectionToolbar } from './ui/SectionToolbar.js';
import { EntityPanel } from './panels/EntityPanel.jsx';
import { ProposalsModal } from './panels/ProposalsModal.jsx';
import { AssistantDock } from './panels/AssistantDock.jsx';
import { FiltersEditor } from './panels/FiltersEditor.jsx';
import { GroupingEditor } from './panels/GroupingEditor.jsx';
import { NodeDisplayEditor } from './panels/NodeDisplayEditor.jsx';
import { DataPanel, MappingEditor } from './panels/DataPanel.jsx';
import { Diagnostics } from './panels/Diagnostics.jsx';
import { PersonPanel } from './panels/PersonPanel.jsx';
import { OrgPanel } from './panels/OrgPanel.jsx';
import { GroupPanel } from './panels/GroupPanel.jsx';
import { PerimeterPanel } from './panels/PerimeterPanel.jsx';
import { TransfersPanel } from './panels/TransfersPanel.jsx';
import { TsaPanel, TsaRegister, type TsaTab } from './panels/TsaPanel.jsx';
import { AnalyticsModal, type AnalyticsTab } from './panels/AnalyticsModal.jsx';
import { HistoryModal } from './panels/HistoryModal.jsx';
import { RefreshModal } from './panels/RefreshModal.jsx';
import { ScenariosModal } from './panels/ScenariosModal.jsx';
import { ExportModal } from './panels/ExportModal.jsx';
import { RemainCoPanel } from './panels/RemainCoPanel.jsx';
import { TargetPanel } from './panels/TargetPanel.jsx';
import { Loading } from './ui/States.js';

/* ------------------------------------------------------------------ */

function Banners(): JSX.Element | null {
  const { error, notice, restored, denied, dismissError, dismissRestored } = useStore();
  if (!error && !notice && !restored && !denied) return null;
  return (
    <div style={{ padding: '10px 18px 0' }}>
      {error && (
        <div className="banner error">
          <span>{error}</span>
          <span className="spacer" />
          <button onClick={dismissError}>Dismiss</button>
        </div>
      )}
      {notice && (
        <div className="banner notice">
          <span>{notice}</span>
          <span className="spacer" />
          <button onClick={dismissError}>Dismiss</button>
        </div>
      )}
      {/*
        FIRST, AND WITH NO DISMISS. Every other banner here reports something that happened to the
        project while this tab went on working; this one reports that the tab has stopped working, and
        nothing it says will stop being true. A Dismiss button would offer to hide the only explanation
        for a screen that no longer responds — and `dismissError` in particular is already bound to three
        unrelated notices, one of which clearing another was a real defect (P5m).
      */}
      {denied && (
        <div className="banner error" data-testid="denied-notice">
          <span>{denied}</span>
        </div>
      )}
      {/*
        Up here rather than beside the panel, because a restore is not about one object. The person
        selected may no longer be in the roster and the scenario open may have come back or gone, so the
        news belongs where it is read before anything else on the screen is trusted.

        Its own Dismiss, not `dismissError`'s. That button is bound to three unrelated notices already,
        and one of them clearing another was a real defect (P5m).
      */}
      {restored && (
        <div className="banner notice" data-testid="restored-notice">
          <span>
            <strong>{restored.actorName}</strong> restored this project (#{restored.seq}):{' '}
            “{restored.summary}”. What you are looking at has been reloaded.
          </span>
          <span className="spacer" />
          <button data-testid="restored-dismiss" onClick={dismissRestored}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function Login(): JSX.Element {
  const login = useStore((s) => s.login);
  const error = useStore((s) => s.error);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
    } catch {
      // The store surfaces the message; nothing to add here.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="centred">
      <form className="card" onSubmit={submit}>
        <h1>Separation Studio</h1>
        <p className="lede">Sign in to your engagement workspace.</p>
        {error && <div className="banner error">{error}</div>}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            data-testid="email"
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            data-testid="password"
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button className="primary" type="submit" data-testid="sign-in" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function ProjectList({ onOpen }: { onOpen(id: string): void }): JSX.Element {
  const projects = useStore((s) => s.projects);
  const importProject = useStore((s) => s.importProject);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    try {
      const name = file.name.replace(/\.xlsx?$/i, '');
      const projectId = await importProject(file, name);
      onOpen(projectId);
    } catch {
      // Surfaced by the store.
    } finally {
      setBusy(false);
    }
  };

  /*
    ARCHIVED ONES BELOW, not hidden and not mixed in.

    Hidden is what the product did — the query parameter existed and nothing sent it — and it makes
    archiving a one-way door, because taking a project OUT of the archive is a command issued from
    inside it. Mixed in is worse: an archived project refuses every write, so opening one expecting to
    work is a screen where nothing responds and nothing says why.
  */
  const live = projects.filter((p: ProjectSummary) => !p.archived);
  const archived = projects.filter((p: ProjectSummary) => p.archived);

  const row = (p: ProjectSummary): JSX.Element => (
    <div className="project-row" key={p.id}>
      <div className="grow">
        <div className="name">{p.name}</div>
        <div className="meta">
          {p.employeeCount} people · {p.dealType} · your role: {p.role} · updated{' '}
          {new Date(p.updatedAt).toLocaleString()}
        </div>
      </div>
      <button
        className={p.archived ? undefined : 'primary'}
        data-testid="open-project"
        data-project-id={p.id}
        onClick={() => onOpen(p.id)}
      >
        Open
      </button>
    </div>
  );

  return (
    <div className="project-list">
      <h2 style={{ marginTop: 0 }}>Projects</h2>
      {projects.length === 0 && (
        <p className="muted">
          No projects yet. Start one from the client&rsquo;s HR extract — an ordinary spreadsheet with
          a row per person — or import a Separation Studio export.
        </p>
      )}
      {live.map(row)}

      {archived.length > 0 && (
        <>
          <h2 style={{ marginTop: 28 }} data-testid="archived-projects">
            Archived
          </h2>
          <p className="muted">
            Read-only, and nothing has been lost. Open one and use Project to take it out of the
            archive.
          </p>
          {archived.map(row)}
        </>
      )}

      <h2 style={{ marginTop: 28 }}>Start a project</h2>
      <div
        className={over ? 'dropzone over' : 'dropzone'}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files[0];
          if (file) void upload(file);
        }}
      >
        {busy ? (
          'Importing…'
        ) : (
          <>
            Drop an <code>.xlsx</code> here — a client HR extract or a Separation Studio export, or{' '}
            <button type="button" onClick={() => fileInput.current?.click()}>
              choose a file
            </button>
          </>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.xls"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const PERSPECTIVE_LABEL = { 'as-is': 'As-is', remain: 'RemainCo', 'carve-out': 'Carve-out' } as const;
const STAGE_LABEL = { perimeter: 'Perimeter', day1: 'Day 1', day2: 'Day 2' } as const;
/** What the boundary calls a workbench that failed. "The workbench" would name none of the three. */
const WORKBENCH_LABEL: Record<ViewName, string> = {
  analytics: 'analytics',
  tsa: 'TSA register',
  entities: 'entity flow',
  transfers: 'transfer picker',
  target: 'target design',
};

/**
 * The other PEOPLE in the room — not the other connections.
 *
 * This filtered on `connectionId` alone, which is the right key for the room and the wrong one for this
 * question. Presence is per connection deliberately, so that one person's two tabs are two entries and
 * the server can tell them apart; the client then has to fold them back, and it did not. Your own second
 * tab therefore read as a colleague: your initials in the avatar row, your name under "Who's here", and —
 * the sharp one — your own pink eye marker sitting on the card your other tab has open, which is the
 * marker that exists to mean "somebody else is looking at this".
 *
 * One entry per user, keeping the FIRST, so a colleague with two tabs is one person rather than two. Which
 * of their tabs supplies `selectedId` is arbitrary, and deliberately so: the alternative is showing one
 * person in two places at once, which is the same untruth from the other side.
 *
 * Exported for the test. There is no seam here otherwise — it is a `useMemo` inside a component that
 * needs a store, a socket and a project to mount, for a two-line derivation.
 */
export function peersOf(members: PresenceMember[], myUserId: string | null): PresenceMember[] {
  const seen = new Set<string>();
  const out: PresenceMember[] = [];
  for (const m of members) {
    if (m.userId === myUserId) continue;
    if (seen.has(m.userId)) continue;
    seen.add(m.userId);
    out.push(m);
  }
  return out;
}

function ProjectView({ onClose }: { onClose(): void }): JSX.Element {
  const project = useStore((s) => s.project)!;
  const decisions = useStore((s) => s.decisions);
  const members = useStore((s) => s.members);
  const myUserId = useStore((s) => s.user?.id ?? null);
  const myUserName = useStore((s) => s.user?.name ?? '');
  const applyOwnProfile = useStore((s) => s.applyOwnProfile);
  // The first reader `user.isAdmin` has ever had: it rode on every session and reached no screen.
  const isAdmin = useStore((s) => s.user?.isAdmin ?? false);
  const conflict = useStore((s) => s.conflict);
  const dismissConflict = useStore((s) => s.dismissConflict);
  const status = useStore((s) => s.status);
  const activity = useStore((s) => s.activity);
  const role = useStore((s) => s.role);
  /*
    The active scenario's governance gate, resolved once beside the role it modulates. From the
    scenario LIST rather than the design payload, for the same reason `archived` travels there: it
    is the one state that changes what the panels below do, and the list is what the client has.
  */
  const perimeterFrozen =
    useStore(
      (s) => s.project?.scenarios.find((sc) => sc.id === s.project?.activeScenarioId)?.perimeterFrozen,
    ) ?? false;
  const loadProjects = useStore((s) => s.loadProjects);
  const seq = useStore((s) => s.seq);
  const pendingCount = Object.keys(useStore((s) => s.pending)).length;
  const setBucket = useStore((s) => s.setBucket);
  const select = useStore((s) => s.select);

  const design = useStore((s) => s.design);
  // Project state, not a lens: the deck reads the same config, so the screen and the slide agree.
  const nodeDisplay = useStore((s) => s.nodeDisplay);
  const pptxGeom = useStore((s) => s.pptxGeom);
  const scenarioName = useStore((s) => s.scenarioName);
  const lens = useStore((s) => s.lens);
  const setLens = useStore((s) => s.setLens);
  const setScenario = useStore((s) => s.setScenario);
  const undoMine = useStore((s) => s.undoMine);
  const phase = useStore((s) => s.phase);
  const setPhase = useStore((s) => s.setPhase);
  const dispatch = useStore((s) => s.dispatch);
  const revert = useStore((s) => s.revert);
  const note = useStore((s) => s.note);
  const restore = useStore((s) => s.restore);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  /*
    THE PANEL'S WIDTH, which was hard-coded at 320px.

    `PersonPanel` is 1,168 lines rendering into that column, and the density complaint the review
    made about this screen mostly reduces to it. Dragging is the whole feature.

    PER TAB AND PER SESSION, not a stored preference. `user_project_prefs` is for choices that
    describe the DESIGN work — which scenario, which filters, which phase — and are worth carrying to
    another machine and into somebody else's reading of the audit trail. How wide a column is on this
    monitor is none of those things, and persisting it would mean a server round trip and a visible
    reflow on every project open.
  */
  const [panelWidth, setPanelWidth] = useState(320);
  const dragFrom = useRef<{ x: number; width: number } | null>(null);

  const onResizeStart = (e: React.PointerEvent<HTMLButtonElement>): void => {
    dragFrom.current = { x: e.clientX, width: panelWidth };
    // Capture, so the drag survives the pointer leaving the 4px handle — which it does immediately.
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onResizeMove = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const from = dragFrom.current;
    if (!from) return;
    // Dragging LEFT widens: the handle is on the panel's left edge, so the delta is inverted.
    const next = from.width + (from.x - e.clientX);
    // Bounded so neither side can be dragged to nothing and left unrecoverable.
    setPanelWidth(Math.max(260, Math.min(760, next)));
  };
  const onResizeEnd = (): void => {
    dragFrom.current = null;
  };
  /*
    A STACK, because a modal can open another one and has to still be there underneath.

    It was a single value, so the "loaded-cost multiplier" link inside the stranded pane replaced the
    analytics modal with the assumptions editor — unmounting every pane's local state, the tab you were
    on and the metric you had chosen — and closing the editor then dropped you all the way back to the
    chart rather than to the pane that sent you. The frozen app stacks: opening the assumptions editor
    only adds a class, and the analytics pane re-renders in place behind it (orgchart.html:6777-6783).

    This is a precondition for P4l rather than a tidy-up. Every pane it adds carries more local state
    than the stranded one, and the coverage matrix's own filter — which opens a second modal from inside
    the first — cannot be built at all while opening one closes the other.
  */
  type ModalName =
    | 'diagnostics'
    | 'mapping'
    | 'refresh'
    | 'scenarios'
    | 'assumptions'
    | 'filters'
    | 'grouping'
    | 'display'
    | 'history'
    | 'members'
    | 'proposals'
    | 'export'
    | 'project-settings'
    /* Not project-scoped, unlike every other entry: it is reachable from the shell too. */
    | 'account';
  const [modals, setModals] = useState<ModalName[]>([]);
  const modal = modals.at(-1) ?? null;

  /*
    The last scheduled review, fetched when the Diagnostics dialog opens and never before.

    NOT PART OF THE PROJECT PAYLOAD, deliberately: it is one small read that only one dialog uses,
    most deployments have no schedule running at all, and putting it in `/state` would put a query
    on the hot path of every refetch to answer a question nobody had asked yet.

    EVERY FAILURE IS `null`. A deployment with no schedule, a project never reviewed, and a
    restricted member the route refuses with a 403 are three different situations and one screen:
    the review the dialog shows is computed client-side from the state already on screen, so there
    is nothing to degrade and nothing worth an error banner. What the run adds is the comparison,
    and "no comparison available" is adequately said by not showing one.
  */
  const [lastReview, setLastReview] = useState<AnalystRunSummary | null>(null);
  useEffect(() => {
    if (modal !== 'diagnostics' || !project) {
      return;
    }
    let live = true;
    void api
      .review(project.projectId)
      .then((r) => {
        if (live) setLastReview(r.run);
      })
      .catch(() => {
        if (live) setLastReview(null);
      });
    return () => {
      live = false;
    };
  }, [modal, project]);
  /** Open one on top of whatever is already there. */
  const openModal = (name: ModalName): void => setModals((m) => [...m, name]);
  /** Close the top one, revealing what sent you there. */
  const closeModal = (): void => setModals((m) => m.slice(0, -1));

  /*
    THE WORKBENCH, which is a place rather than a member of that stack.

    Analytics, the TSA register and the entity flow left `ModalName` here. They are the three heaviest
    surfaces in the product — six analytics panes, a table somebody works through for an hour, and a
    flow diagram with two registers attached — and all three were reached by opening a box on top of
    the thing they describe, capped at 1000px in the middle of a 1400px screen.

    Being a place rather than a stack entry is what gives them an address: `?view=analytics` is in the
    URL, survives a refresh, and is a link somebody can send. It also means the back button leaves
    them, which is what a reader expects of somewhere they navigated TO rather than something that
    interrupted them.

    ONE AT A TIME, and no stack, because a workbench does not open another workbench — you go to the
    next one. The dialogs a workbench opens (the assumptions grid, from two panes inside Analytics)
    are still modals, still stacked, and now correctly sit ON TOP of a place rather than beside it in
    a list where the last one wins.
  */
  /*
    READ FROM THE URL AT BIRTH, not in an effect, and the difference is the whole feature.

    The first version read it in a `useEffect`. Effects run after the first render and in declaration
    order, and the effect that WRITES the address bar is declared above this one — so on a cold load
    of `?view=analytics` the write went first, wrote `view: null` because state said null, and the
    read that followed found a URL it had just cleaned. The link opened the chart. The end-to-end
    check added for exactly this caught it, which is the argument for having written the check as
    "paste a URL into a tab that has never seen this app" rather than as "click, then assert".

    A state initialiser runs BEFORE the first render, so the first write agrees with the URL instead
    of overwriting it. `popstate` still needs an effect — that is a later arrival, not a birth.
  */
  const [view, setView] = useState<ViewName | null>(() => readPlace(window.location).view);
  /** Leaves whichever workbench is open. Named so the surfaces read as navigating, not dismissing. */
  const leaveWorkbench = (): void => setView(null);
  /*
    Which tab a multi-tab modal opens on, kept beside `modal` rather than folded into it.

    It has to be settable independently, because both of the app's entry points into a tabbed analytics
    surface are DEEP links — the RemainCo panel opens the stranded tab by name, a group's restructure pane
    opens the coverage matrix. A control labelled "open the exit timeline" that lands on whatever tab was
    default is the same defect P4i-d and P4i-f exist to close. It is per-user, has no inverse, and narrows
    nothing, so it belongs here beside `selectedId` — never in `lens`, never in the preferences row.
  */
  /*
    The multi-selection: EMPLOYEE ids, held per tab, ephemeral.

    Beside `selectedId` for the reason `tsaTab` gives above — per-user, no inverse, narrows nothing — and
    employee ids rather than chart node ids because group node ids are content-addressed on the level
    path, so a rename or a pivot re-keys them and a stored set would rot invisibly. Person node ids are
    stable but there is no reason to carry the prefix.

    HELD, not resolved. The set survives every lens change, because a lens must lose nothing: clearing a
    filter has to bring people back, or narrowing becomes a destructive edit to the user's own work. What
    may be ACTED on is `selectionRollup(...).resolved` — the subset this view is drawing — and the gap
    between the two is printed rather than hidden. It is pruned for identity only: a person who has left
    the roster, and a scenario switch.
  */
  const [selection, setSelection] = useState<ReadonlySet<string>>(() => new Set());



  /*
    Cleared on a scenario switch, because a selection is a set of decisions-in-progress about ONE design.
    Carrying it across would let a bulk aimed at the carve-out option a user was comparing land on the one
    they switched to. The transfers picker gets this from a React `key`; the set lives up here, so it says
    so itself.
  */
  useEffect(() => {
    setSelection((prev) => (prev.size ? new Set() : prev));
  }, [project?.activeScenarioId]);
  /*
    Both tab states open on what the link named, for the reason `view` gives above: a route that
    restored the surface and not the pane would lose the only part of "look at the stranded pane"
    that carried a meaning.
  */
  const [tsaTab, setTsaTab] = useState<TsaTab>(() => {
    const place = readPlace(window.location);
    return place.view === 'tsa' && (place.tab === 'register' || place.tab === 'economics')
      ? place.tab
      : 'register';
  });
  const chart = useRef<OrgChartHandle>(null);

  /*
    The fetched design with this tab's optimistic decisions laid over it.

    The store keeps buckets separately because that is what an optimistic apply and its rollback operate on;
    the rest of the design comes from the server and is replaced wholesale on a refetch. Overlaying rather
    than mutating means a rollback needs no knowledge of the design at all.
  */
  const liveDesign = useMemo(() => {
    if (!design) return null;
    return { ...design, design: { ...design.design, buckets: decisions } };
  }, [design, decisions]);

  /*
    Resolved against the project on every read, the way the phase is.

    A grouping level can be removed or reordered by somebody else's command while this modal is open, and
    the tab naming it then names nothing. `resolveAnalyticsTab` answers that in one place — the tab if it
    still exists, the project's default otherwise — so no pane has to guard against a level that is gone.
  */
  const [analyticsTabRaw, setAnalyticsTab] = useState<AnalyticsTab>(() => {
    /*
      An unknown tab from the URL is not checked here, deliberately. `resolveAnalyticsTab` runs on
      every read below and falls back to the project's default, because a colleague can delete the
      grouping level a tab names while the pane is open. A link carrying a level that no longer
      exists is that same situation arriving by a different road, and it already has one answer.
    */
    const place = readPlace(window.location);
    if (place.view === 'analytics' && place.tab) return place.tab as AnalyticsTab;
    return liveDesign ? defaultAnalyticsTab(liveDesign) : 'stranded';
  });
  /*
    The scenario count rides along because the design cannot carry it: a `SeparationState` holds one
    design by construction, and the compare tab exists only when there are siblings to compare. The
    same count gates the same tab in the modal's own bar, so control and content cannot disagree.
  */
  const analyticsTab = liveDesign
    ? resolveAnalyticsTab(liveDesign, analyticsTabRaw, { scenarioCount: project?.scenarios.length })
    : analyticsTabRaw;

  /*
    THE ADDRESS BAR, kept in step with where you are.

    Written from state and never read back here — `App` does the reading, once, on first load and on
    `popstate`. Reading it continuously in both components would produce the loop every hand-rolled
    router grows: a state change writes the URL, the write triggers a read, the read re-sets the
    state it came from.

    `replace`, not `push`: clicking through five phases and a dozen people should not bury the
    project list under eighteen back-button presses. What a reader means by "back" here is "out of
    this project", and only opening and closing one is treated as a navigation.
  */
  useEffect(() => {
    if (!project) return;
    syncUrl(
      {
        projectId: project.projectId,
        phase,
        node: selectedId,
        view,
        /*
          Only when a pane has actually been opened: the resolved default is not a choice anybody
          made, and putting it in every link would make two links differ that mean the same thing.

          `tab` belongs to whichever workbench is open — Analytics has six panes and the register has
          two. They cannot both be open, so one field says which pane of the current place you are on
          and a link carries it either way.
        */
        tab: view === 'analytics' ? analyticsTab : view === 'tsa' ? tsaTab : null,
      },
      'replace',
    );
  });

  /*
    And read back on `popstate`, which the address bar was not until now.

    P9c-a wrote the URL and read only the project id from it, so `?node=` and `?tab=` were a
    description of where you were rather than a way to get there. A workbench with no read is worse
    than none: the link says `?view=analytics`, the reader opens it, and lands on the chart.

    `popstate` ONLY here — the cold-load read is the state initialiser above, for the ordering reason
    given there. Reading continuously in an effect would fight every setter: a state change writes the
    URL, the write triggers a read, and the read re-sets the state it came from.

    THIS IS WHAT MAKES BACK WORK. A workbench replaces the URL rather than pushing one, so the entry
    it lands on is the project — and "back" from the register means the chart, which is exactly what
    a reader expects of somewhere they navigated to.
  */
  useEffect(() => {
    const apply = (): void => {
      const place = readPlace(window.location);
      // Idempotent: setting `view` to what it already is still re-renders the shell.
      setView((current) => (current === place.view ? current : place.view));
      if (place.tab === null) return;
      if (place.view === 'analytics') setAnalyticsTab(place.tab as AnalyticsTab);
      else if (place.view === 'tsa' && (place.tab === 'register' || place.tab === 'economics')) {
        setTsaTab(place.tab);
      }
    };
    window.addEventListener('popstate', apply);
    return () => window.removeEventListener('popstate', apply);
  }, []);

  /*
    The roster comes from the design, not from `/state`.

    They are the same people, but the design's records are the full ones — levels, supervisor, entity,
    leaver status — and a panel reading the narrow copy would quietly show a subset while claiming to
    show the person. Empty until the design lands, which is what the loading guard below is for.
  */
  const employees = liveDesign?.employees ?? [];
  const selectedEmployee = useMemo(() => {
    // Group and root nodes are selectable too; only a person node resolves to someone to edit.
    const id = selectedId ? employeeIdFromNode(selectedId) : null;
    if (!id) return null;
    /*
      Build-ups are searched too, and that is not a nicety.

      `buildHierarchy` draws them (packages/core/src/hierarchy.ts:172 concatenates them into the
      population), so a planned hire has a card on the chart like anybody else. Looking them up in the
      roster alone meant clicking that card resolved to nobody, fell through to the "selectable but
      neither" branch below, and answered with the whole-organisation panel — while the person panel's
      build-up section sat behind a condition that could never be true.
    */
    return (
      employees.find((e) => e.id === id) ??
      liveDesign?.design.buildUps.find((b) => b.id === id) ??
      null
    );
  }, [selectedId, employees, liveDesign]);

  /*
    The tree, built once and shared.

    The chart draws it and the group panel reads its rollups off it — a group's headcount, FTE and cost
    are exactly what the card on the chart says, so computing them a second time would eventually let the
    panel disagree with the box the user just clicked.
  */
  /*
    The lens the tree is built under, held once rather than rebuilt per consumer.

    The chart needs the same object: a card resolves its values through the perspective and its dimming
    through the view mode, and a second copy assembled at the call site is a second chance to disagree
    with the tree it is drawing.
  */
  /*
    The blueprint, and only when there is one. `buildHierarchy` trusts `targetView` without consulting
    `targetEnabled`, and turning the design off deliberately keeps the units, the ladder and every
    placement — so a lens that said "target" while the design was off would render a blueprint the
    project says it is not using.
  */
  const targetActive = !!liveDesign?.targetEnabled && lens.targetView;

  /*
    Defaulted once: every reader of the display config wants a complete object, and a project that has
    never stored one is a project running on the defaults.

    Memoised because it is a DEPENDENCY of the chart effect, and `freshNodeDisplay()` returns a new object
    every call. `Object.is` on a fresh object is false, so an unmemoised default made the chart re-run its
    whole `data().render()` on every unrelated render of this component. Inert while the server always
    returns a complete NodeDisplay, and one line to make it stay inert.
  */
  const cards = useMemo(() => nodeDisplay ?? freshNodeDisplay(), [nodeDisplay]);

  /*
    Pruned for IDENTITY, never for visibility.

    Somebody a data refresh removed, or a planned hire somebody deleted, is gone — holding their id would
    let a later bulk name a person who is not there. Somebody a FILTER removed is still on the roster, and
    dropping them here would make narrowing a destructive edit to the user's own selection; they are
    counted as hidden and printed instead. Same shape as the transfers picker's prune, and keyed the same
    way: on the design, so it runs when the roster actually changes rather than on every render.
  */
  useEffect(() => {
    if (!liveDesign) return;
    setSelection((prev) => {
      if (!prev.size) return prev;
      const onRoster = new Set<string>();
      for (const person of liveDesign.employees) onRoster.add(String(person.id));
      for (const hire of liveDesign.design.buildUps) onRoster.add(String(hire.id));
      const still = new Set<string>();
      for (const id of prev) if (onRoster.has(id)) still.add(id);
      return still.size === prev.size ? prev : still;
    });
  }, [liveDesign]);

  const chartLens = useMemo(
    () =>
      liveDesign
        ? {
            ...freshLens(),
            perspective: lens.perspective,
            stage: lens.stage,
            /*
              The blueprint, and only when there is one.

              `buildHierarchy` trusts this field without consulting `targetEnabled`, and turning the
              design off deliberately keeps the units, the ladder and every placement — so a lens that
              said "target" while the design was off would render a blueprint the project says it is
              not using. The conjunction is the check the engine does not make.

              `viewGrouping` stays null alongside it: `getDisplayLevels` short-circuits on `targetView`
              first, but `isCustomPivot` is read independently further down and changes the ragged-path
              rule, so a pivot and the target view together would draw something neither describes.
            */
            targetView: targetActive,
            // This tab's filters, from the lens — never off the design. The design is what everyone
            // sees; the narrowing is this person's alone.
            filters: lens.filters,
            viewMode: lens.viewMode,
            /*
              Both pinned while the blueprint is on, because `buildHierarchy` cannot resolve either
              conflict itself. It returns the reporting tree BEFORE it reads `targetView`, so reporting
              silently wins and the Target-view button reads pressed over the real reporting line; and
              `getDisplayLevels` short-circuits on `targetView` while `isCustomPivot` is read
              independently further down, where it changes the ragged-path rule — so a blueprint drawn
              under a pivot uses the pivot's "(No X)" bucketing. The deck forces exactly this
              (`deck-tree.ts`); the screen now does the same, and the layout control says so rather
              than being quietly ignored.
            */
            viewLayout: targetActive ? 'grouping' : lens.viewLayout,
            viewGrouping: targetActive ? null : lens.viewGrouping,
            /*
              The one display option that changes the TREE rather than the card.

              `wantStageCounts` gates whether `buildHierarchy` computes the per-group stage triples at
              all — it is a whole extra pass over the roster, so it is off unless something asks. This
              was pinned false by `freshLens()`, so ticking "stage comparison" would have landed the
              command, started the deck drawing stage lines, and shown nothing on screen: the data was
              never computed. Derived the same way the app (`orgchart.html:3821`) and the deck both do.
            */
            wantStageCounts: !!(cards.group.stageCounts || cards.root.stageCounts),
          }
        : freshLens(),
    [liveDesign, lens, targetActive, cards],
  );

  const nodes = useMemo(
    () => (liveDesign ? buildHierarchy(liveDesign, chartLens) : []),
    [liveDesign, chartLens],
  );

  /*
    What the held selection means in the view on screen, computed ONCE and shared.

    The toolbar pill and the selection panel both print counts from this, so they cannot disagree about how
    many people are selected — which is the failure mode when two surfaces each resolve the same set.
    `resolved` is also what every bulk command is built from: the held set may name somebody a filter is
    hiding, and acting on people who are not on screen is the frozen app's worst behaviour here.
  */
  const rollup = useMemo(
    () => (liveDesign ? selectionRollup(liveDesign, nodes, selection) : null),
    [liveDesign, nodes, selection],
  );

  const selectedGroup = useMemo(() => {
    if (!selectedId) return null;
    const found = nodes.find((n) => n.id === selectedId);
    return found?.kind === 'group' ? found : null;
  }, [nodes, selectedId]);

  /*
    Others' presence, excluding this tab.

    Memoised for the same reason, and here it was not inert: `.filter` returns a new array every render,
    `peers` is passed as `members` to OrgChart, and `props.members` is in that effect's dependency list —
    so the chart tore down and rebuilt every card on every render of this component. A modal opening, an
    activity event, another user's cursor moving. That is wasteful on its own, and it is what makes an
    imperative class toggle on the cards unsafe: `restyleForeignObjectElements` rewrites every card's
    innerHTML on each render, so anything toggled outside React's knowledge is wiped mid-interaction.
  */
  const peers = useMemo(() => peersOf(members, myUserId), [members, myUserId]);
  /*
    What to CALL the node each colleague has open, resolved from the id they announced.

    The roster used to say "· viewing a node" and stop there, though it had the id all along. Naming it
    from the id rather than by looking it up in the drawn tree is what makes the answer survive the four
    ways that lookup goes silent: the node filtered out, hidden by the view mode, collapsed, or — for a
    group — simply absent because their lens produced a path yours does not have.

    Over the UNFILTERED roster deliberately: a colleague reading somebody your own filters hide is exactly
    when you most want to know who they are looking at.
  */
  const nameOfEmployee = useMemo(() => {
    const byId = new Map<string, string>();
    for (const e of employees) byId.set(String(e.id), String(e.name));
    for (const b of liveDesign?.design.buildUps ?? []) byId.set(String(b.id), String(b.name));
    return (id: string): string | undefined => byId.get(id);
  }, [employees, liveDesign]);

  /*
    How much of the roster the chart is actually drawing.

    The bucket pills beside this count the WHOLE roster — they always have, in both apps, because a tally
    that moved with the filter would answer a different question every time somebody narrowed the view.
    The consequence is that with a filter on, three pills describe nine hundred people over a chart
    drawing forty, and nothing on screen reconciled them. The frozen app says so (`orgchart.html:4791`,
    "Showing X of Y") and the port dropped the sentence while keeping the pills.

    Same population on both sides as the app uses: people AND planned hires, since `buildHierarchy` draws
    both and a reader counting cards is counting both. The filter pill next to it says a lens is on; this
    says what the lens costs.
  */
  const drawn = useMemo(() => nodes.filter((n) => n.kind === 'person').length, [nodes]);
  const population = employees.length + (liveDesign?.design.buildUps.length ?? 0);

  const tally = useMemo(() => {
    const counts: Record<Bucket, number> = { Remain: 0, 'Carve-out': 0, 'No longer required': 0 };
    for (const e of employees) {
      if (!countsInHeadcount(e)) continue;
      counts[decisions[e.id] ?? 'Remain'] += 1;
    }
    return counts;
  }, [employees, decisions]);

  const readOnly = !role || !canWrite(role);
  // A DEPLOYMENT fact, not a project one: learned once at sign-in and held on the store.
  const assistantAvailable = useStore((s) => s.assistantAvailable);
  const voiceAvailable = useStore((s) => s.voiceAvailable);
  const realtimeAvailable = useStore((s) => s.realtimeAvailable);

  /*
    Ctrl-Z, restored.

    The plan specified "per-user undo (Ctrl-Z), by inverse command" and the frozen app implements it
    (`orgchart.html:10880`). The React client had neither: a `grep` for `addEventListener` over
    `apps/web/src` returned only the store's `online` and `visibilitychange` probes, and the only undo
    control was a button inside the History modal. So "take back what I just did" cost opening the log,
    ticking "only my changes", finding your row and clicking — per step.

    THE EXCLUSIONS ARE THE DESIGN, and they are the reason this is a keydown handler rather than a
    binding on the chart:

     · A FIELD HAS ITS OWN UNDO. Inside an input, a textarea, a select or anything contentEditable,
       Ctrl-Z means "undo my typing" and the browser already does it; stealing it there would take back a
       colleague-visible decision because somebody wanted a character back.
     · A MODAL OWNS ITS KEYS. The History modal has explicit Undo buttons and its own refusal sentences;
       firing a blind undo behind it would act on a row the reader is not looking at.
     · A READER HAS NOTHING TO UNDO. Gated on the same `readOnly` every other write is.

    Registered on `document` rather than on a container, because the gesture belongs to the page and the
    chart may not have focus — which is exactly the state somebody is in after clicking a card and
    changing a bucket from the panel.
  */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z' || e.altKey) return;
      // Shift+Ctrl+Z is redo in every editor there is. There is no redo here — a revert is itself a
      // change, so redoing means reverting the revert — and quietly treating it as undo would be worse
      // than doing nothing.
      if (e.shiftKey) return;
      if (readOnly || modal !== null) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      void undoMine();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [readOnly, modal, undoMine]);


  // How many conditions actually constrain anything — `filters.length` over-reports a condition that
  // was added and never given a value, putting a badge on a chart nothing is hiding.
  const activeCount = activeFilterCount(lens.filters);

  /*
    What the reporting layout had to invent to draw one tree.

    It re-parents anybody whose manager it cannot see — a supervisor id that resolves to nobody, a
    manager the lens or a filter removed, a mutual cycle — and every one of those produces a plausible
    organisation that never existed. Narrow to one country with the reporting line on and that
    country's managers' reports redraw as though they reported to the board. The engine counts them
    where it already knows; this is the reporting.
  */
  const integrity = useMemo(() => {
    const root = nodes.find((n) => n.kind === 'root');
    const report = root?.kind === 'root' ? root.data.reportingIntegrity : null;
    if (!report) return null;
    const total = report.orphans + report.spliced + report.withheld + report.cycles;
    return total > 0 ? { ...report, total } : null;
  }, [nodes]);

  /*
    Which pane the aside shows. State, not a derivation, and the difference matters.

    It began as `selectedId === null`, which is exactly as far as two states go: the aside shows the phase
    you are working in or the thing you selected, never both — the app's own rule (`openPhasePanel` clears
    the selection at orgchart.html:5926). A multi-selection is a THIRD occupant, so a boolean would render
    two of them stacked.

    But it cannot be DERIVED from the other two either. `(selectedId === null, selection.size === 40)` is
    the state immediately after a ctrl-click — selection panel showing — AND the state after the user then
    clicks a phase, where the phase panel shows while the pill still reads 40 and the cards stay badged.
    Keeping a selection across a phase change is the frozen app's behaviour, and it distinguishes the two
    with two explicit flags for the same reason (`_selectionPanelOpen`, `_phasePanelOpen`).
  */
  const [pane, setPane] = useState<'phase' | 'node' | 'selection'>('phase');

  /*
    Selection mode: plain clicks select instead of opening a panel.

    The frozen app's discoverable entry point — a toolbar toggle — and the thing its Perimeter panel copy
    points at. Worth having beyond discoverability: it makes the whole gesture reachable without a
    modifier key, which is what lets the e2e drive it, since the suite dispatches synthetic clicks.
  */
  const [selectMode, setSelectMode] = useState(false);

  /**
   * Open somebody's panel by employee id, as a panel would rather than as the chart does.
   *
   * The chart selects a NODE; a panel knows a person. Going through `personNodeId` keeps the one
   * encoding of that in core, so a newly created build-up lands on its own panel with the chart's
   * selection agreeing rather than the two disagreeing about what is open.
   */
  const selectPerson = (employeeId: string): void => {
    const nodeId = personNodeId(employeeId);
    setSelectedId(nodeId);
    select(nodeId);
    setPane('node');
  };

  /*
    Extend or shrink the selection from one chart node.

    The node is resolved to PEOPLE here, through the one function in core, so a person card, a group card
    and the root card are the same gesture at three scopes: yourself, your team (or your whole reporting
    line, in the reporting layout), everyone.

    `allIn` is the frozen app's semantics: re-clicking a fully selected team deselects it, while a
    partially selected team gets the rest added rather than partially removed. Without it, ctrl-clicking a
    group you had half-selected would toggle each member independently and leave a set nobody chose.

    A selection click sends no presence frame and clears `selectedId`: presence is a cursor — where one
    person is looking — and a 40-person selection is not a place.
  */
  const toggleSelect = (nodeId: string): void => {
    const ids = subtreePeopleIds(nodes, nodeId);
    if (!ids.length) return;
    setSelection((prev) => {
      const next = new Set(prev);
      const allIn = ids.every((id) => next.has(id));
      for (const id of ids) {
        if (allIn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    setSelectedId(null);
    select(null);
    setPane('selection');
  };
  const choosePhase = (id: typeof phase): void => {
    setPhase(id);
    setSelectedId(null);
    select(null);
    // The selection SURVIVES — a phase is where you are working, not who you have picked. Only the pane
    // changes, which is why it is state rather than something derived from the set being non-empty.
    setPane('phase');
  };

  return (
    // The sequence number is on the root rather than on screen: it is the project's version, which
    // matters to anything checking that a change did or did not happen, and to nobody reading the page.
    <div className="app" data-seq={seq} data-phase={phase}>
      {/*
        THE HEADER IS WHERE YOU GO; the bar under the chart is what you are looking at.

        Both used to be one row of twenty-three controls that wrapped at 1400px. The split is by
        question rather than by frequency: "open another surface" (here) and "change this view of
        this chart" (the canvas bar) are different intentions, and a reader scanning for one was
        reading past the other. Nothing is hidden — every control that was reachable is still
        reachable in one click, which is also what keeps the end-to-end suite honest about this.
      */}
      <header className="header">
        <div className="identity">
          <h1>{project.name}</h1>
          <span className="pill">{project.dealType}</span>
        </div>

        <span className={`pill ${status}`} data-testid="conn-status" data-status={status}>
          <span className="dot" />
          {status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : status === 'reconnecting' ? 'Reconnecting…' : status === 'offline' ? 'Offline — retrying' : status === 'denied' ? 'No longer connected' : 'Idle'}
        </span>
        {pendingCount > 0 && <span className="pill">Saving {pendingCount}…</span>}

        <span className="spacer" />

        <nav className="destinations" aria-label="Go to">
          {/*
            A command, not a lens — so unlike the filter and grouping buttons beside it, this one is
            disabled for a reader. Narrowing your own view is reading; changing what everybody's cards
            show is not.
          */}
          {liveDesign && (
            <button
              data-testid="open-node-display"
              disabled={readOnly}
              title="What the cards show — shared with the project, and read by the deck"
              onClick={() => openModal('display')}
            >
              Card display
            </button>
          )}
          {/*
            Deliberately NOT disabled for a reader, which is the contrast the button above draws in the
            other direction: a read-only pane is reading, and reading is exactly what a reviewer is for.
          */}
          {/*
            Not disabled for a reader: the flow diagram is a read, and the controls inside carry
            `readOnly` themselves. Sits beside Analytics because that is what it mostly is — with two
            registers attached.
          */}
          {liveDesign && (
            <button
              data-testid="open-entities"
              title="Which legal entity each person is employed by, and which one they move to"
              onClick={() => setView('entities')}
            >
              Entities
            </button>
          )}
          {liveDesign && (
            <button
              data-testid="open-analytics"
              title="Read-only cost and headcount panes"
              onClick={() => setView('analytics')}
            >
              Analytics
            </button>
          )}
          {/*
            Also not disabled for a reader. The log itself is reading, and a reviewer's whole job is to see
            what has been decided and by whom; the undo button inside is what the role gates.
          */}
          {project && (
            <button
              data-testid="open-history"
              title="Every change to this project, and what can be undone"
              onClick={() => openModal('history')}
            >
              History
            </button>
          )}
          {/*
            Also open to a reader. An export is a read of the project, and the role that exists to review
            a design is the one most likely to want it as a deck.
          */}
          {liveDesign && (
            <button
              data-testid="open-export"
              title="Download the project as a workbook or a deck"
              onClick={() => openModal('export')}
            >
              Export
            </button>
          )}
          {/*
            Open to a reader too, and for a sharper reason than the two above: these names are already on
            the presence strip and in every change-log row, so withholding the list protects nothing and
            only stops a reviewer working out who to ask. What the role gates is inside — an owner gets the
            controls, everybody else gets the roster.
          */}
          {project && (
            <button
              data-testid="open-members"
              title="Who can see this project, and what they can do"
              onClick={() => openModal('members')}
            >
              People
            </button>
          )}
          {/*
            The project itself — its name, the archive, and deleting it. Shown to everybody and gated
            inside, the same shape as People and Account: an owner gets the controls and everybody else
            gets a sentence saying who can. Three server capabilities reached nothing before this.
          */}
          {project && (
            <button
              data-testid="open-project-settings"
              title="Rename, archive or delete this project"
              onClick={() => openModal('project-settings')}
            >
              Project
            </button>
          )}
          {/*
            Reachable from INSIDE a project as well as from the project list, because the two are one
            errand: `MembersEditor` grants access by email and can only reach an account that already
            exists, so an owner adding a colleague who has never signed in needs to create them without
            leaving what they are doing. Open to everybody — a non-administrator gets their own
            password and nothing else.
          */}
          <button
            data-testid="open-account"
            title="Your password, and — if you administer this deployment — everybody's account"
            onClick={() => openModal('account')}
          >
            Account
          </button>
          {/*
            Open to every member, like History and for the same reason: a proposal is a conversation about
            the project. Applying one is the write, and that is gated inside.
          */}
          {/*
            THE ASSISTANT IS NOT IN THIS BAR ANY MORE — see `AssistantDock`, mounted below. It used to be
            a button here that opened a modal over the chart, which was the wrong shape twice over: a
            modal is an interruption you complete and dismiss, and this header already carries nine
            controls. The gate that used to live on this line moved to the launcher verbatim.
          */}
          {project && (
            <button
              data-testid="open-proposals"
              title="Changes somebody has suggested but not made"
              onClick={() => openModal('proposals')}
            >
              Proposals
            </button>
          )}
        </nav>

        <div className="who-strip">
          <div className="avatars" data-testid="peers" data-count={peers.length} title={peers.map((p) => p.name).join(', ')}>
            {peers.map((p) => (
              <div className="avatar" key={p.connectionId}>
                {initials(p.name)}
              </div>
            ))}
          </div>
          <span className="who">{role}</span>
          <ThemeToggle />
          <button onClick={onClose}>All projects</button>
        </div>
      </header>

      {liveDesign && <PhaseBar state={liveDesign} phase={phase} onSelect={choosePhase} />}

      {/*
        `data-testid` for one assertion and one only: that this row does not overflow horizontally at
        1400x900 with every conditional control showing. It is a wp11 claim the platform inherited and
        never checked, and the only way to check it is to compare `scrollWidth` with `clientWidth` on
        the element itself.
      */}
      {/*
        HIDDEN WHILE A WORKBENCH IS OPEN, because every control on it acts on the chart — perspective,
        stage, out-of-scope, target view, Fit — and the chart is not on screen. A row of live controls
        for something you cannot see is the shape of interface this milestone exists to remove; it is
        also how somebody flips the perspective, walks back to the canvas and finds it changed with no
        memory of doing it.

        The phase spine above stays, because a phase is where you ARE and a workbench is a detour from
        it, not a replacement for it.
      */}
      {view === null && (
      <div className="viewbar" data-testid="toolbar">
        {/*
          The perspective switcher: the single most-used control in the shipped app, and the first thing the
          client could not do until it held the whole design. It is a lens, so it changes what THIS tab sees
          and emits nothing — two people can read the same project from different sides at the same time.
        */}
        <span className="lens" role="group" aria-label="Perspective">
          {(['as-is', 'remain', 'carve-out'] as const).map((perspective) => (
            <button
              key={perspective}
              data-testid={`perspective-${perspective}`}
              className={lens.perspective === perspective ? 'lens-on' : ''}
              aria-pressed={lens.perspective === perspective}
              onClick={() => setLens({ perspective })}
            >
              {PERSPECTIVE_LABEL[perspective]}
            </button>
          ))}
        </span>
        {/*
          Grey vs hide only means anything off the as-is view: `isVisible` returns true for everyone on
          as-is, so there is nobody out of scope to grey. Held to the same rule as the stage group below.
        */}
        {lens.perspective !== 'as-is' && (
          <span className="lens" role="group" aria-label="Out of scope">
            {(['hide', 'grey'] as const).map((mode) => (
              <button
                key={mode}
                data-testid={`view-mode-${mode}`}
                className={lens.viewMode === mode ? 'lens-on' : ''}
                aria-pressed={lens.viewMode === mode}
                title={
                  mode === 'hide'
                    ? 'Remove people outside this perspective'
                    : 'Keep them on screen, dimmed — every count above them still includes them'
                }
                onClick={() => setLens({ viewMode: mode })}
              >
                {mode === 'hide' ? 'Hide' : 'Grey'}
              </button>
            ))}
          </span>
        )}
        {/*
          Stage only means anything on the carve-out side: it is the Day 1 / Day 2 view of who has moved,
          and on the as-is or Remain view there is nothing for it to slice.
        */}
        {lens.perspective === 'carve-out' ? (
          <span className="lens" role="group" aria-label="Stage">
            {(['perimeter', 'day1', 'day2'] as const).map((stage) => (
              <button
                key={stage}
                data-testid={`stage-${stage}`}
                className={lens.stage === stage ? 'lens-on' : ''}
                aria-pressed={lens.stage === stage}
                onClick={() => setLens({ stage })}
              >
                {STAGE_LABEL[stage]}
              </button>
            ))}
          </span>
        ) : null}
        {/*
          The blueprint switch. A lens, not a command: it emits nothing, nobody else sees it, and it
          only exists when there is a design to draw. `setPhase` turns it on with the Target phase and
          off when you leave, so this is the escape hatch for looking at the blueprint from anywhere.
        */}
        {liveDesign?.targetEnabled && (
          <span className="lens" role="group" aria-label="View">
            <button
              data-testid="target-view-toggle"
              className={lens.targetView ? 'lens-on' : ''}
              aria-pressed={lens.targetView}
              title="Draw the designed organisation rather than the real one"
              onClick={() => setLens({ targetView: !lens.targetView })}
            >
              Target view
            </button>
          </span>
        )}
        {/*
          WHICH SCENARIO THIS READER IS IN, and the only affordance for changing it.

          `ExportModal` has told people to "switch scenario to export a different one" since P4 — an
          instruction for an action with no control anywhere in the app. The design endpoint has always
          taken a `scenarioId`; what was missing was somewhere to say it and somewhere to remember it.

          PER USER, so this is not a lens toggle in disguise: the choice persists to the preference row,
          two people can be in different scenarios on the same project at the same time, and the presence
          strip now has a real answer to put in the `scenarioId` it has always carried.
        */}
        {/*
          Rendered whenever there IS a project, not only when there are two scenarios.

          The count guard used to be the whole condition, and with nothing able to create a second
          scenario it meant this group never appeared — so the one control that could have made a second
          was behind a test requiring one to already exist. The SELECT still hides at one, because a
          picker with a single option is noise; the Scenarios button does not.
        */}
        {project && (
          <span className="lens" role="group" aria-label="Scenario">
            <label htmlFor="scenario-switch" className="muted small">
              Scenario
            </label>
            {project.scenarios.length > 1 && (
            <select
              id="scenario-switch"
              data-testid="scenario-switch"
              value={project.activeScenarioId}
              title="Which design you are looking at. Yours alone — a colleague can be in another."
              onChange={(e) => void setScenario(e.target.value)}
            >
              {project.scenarios.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name}
                  {sc.isDefault ? ' · default' : ''}
                </option>
              ))}
            </select>
            )}
            <button
              data-testid="open-scenarios"
              title="Create, duplicate, rename or delete a design"
              onClick={() => openModal('scenarios')}
            >
              Scenarios{project.scenarios.length > 1 ? ` (${project.scenarios.length})` : ''}
            </button>
          </span>
        )}
        {liveDesign && (
          <span className="lens" role="group" aria-label="Grouping">
            <button
              data-testid="open-grouping"
              data-pivot-levels={lens.viewGrouping.length}
              data-layout={targetActive ? 'grouping' : lens.viewLayout}
              className={lens.viewGrouping.length || lens.viewLayout === 'reporting' ? 'lens-on' : ''}
              title="Choose the layout, or pivot the chart onto fields of your own"
              onClick={() => openModal('grouping')}
            >
              Group by{lens.viewGrouping.length ? ` (${lens.viewGrouping.length})` : ''}
            </button>
          </span>
        )}
        {liveDesign && (
          <span className="lens" role="group" aria-label="Filters">
            <button
              data-testid="open-filters"
              data-active-count={activeCount}
              className={activeCount ? 'lens-on' : ''}
              title="Narrow the chart, the transfers picker and the carrier list together"
              onClick={() => openModal('filters')}
            >
              Filters{activeCount ? ` (${activeCount})` : ''}
            </button>
            {/*
              One click clears everything, with no confirmation — the app's own affordance (`:4826`).
              Safe precisely because a filter is not a change: nothing is lost, and setting it again is
              the same two clicks it took the first time.
            */}
            {activeCount > 0 && (
              <button data-testid="filter-pill" onClick={() => setLens({ filters: [] })}>
                Clear filters
              </button>
            )}
            {/*
              One click back from an aggressive lens, with no confirmation — the app's own affordance
              (`:4795`), safe for the same reason the filter pill is: a lens loses nothing.
            */}
            {lens.viewGrouping.length > 0 && (
              <button data-testid="pivot-pill" onClick={() => setLens({ viewGrouping: [] })}>
                Clear pivot
              </button>
            )}
            {lens.viewLayout === 'reporting' && !targetActive && (
              <button data-testid="reporting-pill" onClick={() => setLens({ viewLayout: 'grouping' })}>
                Back to org structure
              </button>
            )}
          </span>
        )}
        {integrity && (
          <span
            className="pill warn"
            data-testid="chart-integrity-chip"
            data-orphans={integrity.orphans}
            data-spliced={integrity.spliced}
            data-withheld={integrity.withheld}
            data-cycles={integrity.cycles}
            title={
              `${integrity.spliced} report(s) attached above their own manager because the lens or a ` +
              `filter removed them, ${integrity.orphans} name a supervisor who is not in the roster, ` +
              /*
                A FOURTH SENTENCE, WORDED FOR A DIFFERENT READER. The other three are about the file
                or about the lens — things the person looking can investigate or clear. This one is
                about THEM, and it is the only one they cannot act on, so it says whose doing it is
                rather than describing a defect that is not there.
              */
              `${integrity.withheld} report to somebody outside the part of the organisation you can ` +
              `see, and ${integrity.cycles} were re-rooted to break a reporting cycle.`
            }
          >
            ⚠ {integrity.total} reporting {integrity.total === 1 ? 'line' : 'lines'} inferred
          </span>
        )}
        {/* Held back until the roster is here: a tally of nothing reads as a tally of zero. */}
        {liveDesign &&
          BUCKETS.map((bucket) => (
            <span className={`pill bucket-${bucket.replace(/\s+/g, '-')}`} key={bucket}>
              <span className="dot" style={{ color: 'var(--bucket)' }} />
              {bucket}: <strong>{tally[bucket]}</strong>
            </span>
          ))}
        {/*
          Always present once the roster is, not only under a filter: "Showing 900 of 900" is the sentence
          that makes "Showing 40 of 900" legible when it appears. A pill that shows up only when something
          is narrowed teaches nobody what it means.
        */}
        {liveDesign && (
          <span
            className={`pill${drawn < population ? ' warn' : ''}`}
            data-testid="showing-pill"
            data-drawn={drawn}
            data-population={population}
            title={
              drawn < population
                ? `The chart is drawing ${drawn} of ${population}. The bucket counts beside this are the whole roster, not the view.`
                : 'The chart is drawing the whole roster.'
            }
          >
            Showing {drawn} of {population}
          </span>
        )}
        {/*
          The selection pill, in TWO parts.

          The frozen app's is one destructive pill: clicking "40 selected · clear" destroys the selection,
          and the only other routes back to the panel were ctrl-clicking somebody (which changes what is
          selected) or Escape (which also destroys it, silently, with no undo). So the count half here is
          idempotent — it reopens the panel and changes nothing — and clearing is its own button.
        */}
        {rollup && selection.size > 0 && (
          <span className="lens" role="group" aria-label="Selection">
            <button
              data-testid="selection-pill"
              data-selected-count={rollup.people}
              data-selected-held={selection.size}
              data-selected-hidden={rollup.hiddenByLens}
              onClick={() => {
                setSelectedId(null);
                select(null);
                setPane('selection');
              }}
            >
              {rollup.people} selected
            </button>
            <button
              data-testid="selection-pill-clear"
              onClick={() => {
                setSelection(new Set());
                setPane('phase');
              }}
            >
              Clear
            </button>
          </span>
        )}
        {liveDesign && (
          <button
            data-testid="select-mode"
            aria-pressed={selectMode}
            className={selectMode ? 'lens-on' : ''}
            title="Plain clicks build a selection instead of opening a panel. Ctrl/Cmd/Shift-click works either way."
            onClick={() => setSelectMode((v) => !v)}
          >
            ✓ Select
          </button>
        )}
        <button data-testid="expand-all" onClick={() => chart.current?.expandAll()}>
          Expand all
        </button>
        <button data-testid="collapse-all" onClick={() => chart.current?.collapseAll()}>
          Collapse
        </button>
        <button data-testid="fit" onClick={() => chart.current?.fit()}>
          Fit
        </button>
        {readOnly && <span className="pill">Read-only</span>}
        {/*
          The second copy, on the chart. The workbench carries the other one, and both are needed:
          the chart is where the group cards show headcount and cost roll-ups, and somebody who never
          opens the analytics workbench would otherwise never be told.
        */}
        <ScopeNotice where="chart" />
      </div>
      )}

      <Banners />

      <div className={view !== null ? 'main has-workbench' : 'main'}>
        {/*
          THE CANVAS, WITH A WORKBENCH OVER THE WHOLE OF IT.

          A workbench takes the entire main area — the full width of the window, not the 1000px a
          dialog capped it at — which is the visible half of this milestone.

          THE CHART STAYS MOUNTED BEHIND IT, and that is a correction rather than a preference. The
          first version unmounted it, on the reasoning that laying out a d3 chart nobody can see costs
          something and that expansion survives a remount anyway. The second half was an assumption
          and it was wrong: the end-to-end suite went to look for a person's card after a visit to
          Analytics and could not find one, because a remounted chart comes back collapsed. Somebody
          who expands three levels, opens the register for ten minutes and comes back would have found
          their chart reset — with their zoom and pan gone too. The layout cost is real and it is the
          cost the modal already paid; losing a reader's place is not a cost worth paying to avoid it.

          Hidden with `visibility`, not `display`, for the same reason: `display: none` collapses the
          box to nothing, and d3-org-chart measures its container. `visibility: hidden` keeps every
          dimension intact while taking the chart out of the tab order and out of the accessibility
          tree, so nothing behind the workbench can be reached or announced.
        */}
        {view !== null && (
          <ErrorBoundary
            /*
              Panel scope, the same bargain the side panel makes: a surface that cannot compute takes
              itself down and leaves the shell, the connection and everybody else's presence alive.
              The label names the place, because "the workbench" tells a reader nothing about which
              of three they were in.
            */
            scope="panel"
            label={WORKBENCH_LABEL[view]}
          >
            {/*
              The design has to be here before any of these can draw, and it may not be: a link
              straight to `?view=analytics` lands while the fetch is still in flight. Same message the
              canvas gives, for the same reason — a blank main area with no explanation is the one
              outcome a route this direct must not produce.
            */}
            {liveDesign === null ? (
              <div className="chart-loading">Loading the design…</div>
            ) : view === 'tsa' ? (
              <TsaRegister
                state={liveDesign}
                readOnly={readOnly}
                scenarioId={project.activeScenarioId}
                tab={tsaTab}
                onTab={setTsaTab}
                onDispatch={dispatch}
                onOpenAssumptions={() => openModal('assumptions')}
                onClose={leaveWorkbench}
              />
            ) : view === 'transfers' ? (
              /*
                WRAPPED HERE, unlike the other three, and the asymmetry is the honest shape.

                Analytics, the register and the entity flow were dialogs: they already owned a title
                and a close button, so they render their own `Workbench` and swapping the primitive
                was the whole edit. These two are side-panel panels — they render a bare fragment of
                sections and have never had a chrome of their own. Pushing one into them so the two
                cases matched would put a title bar inside a component that renders into a column
                without one.
              */
              <Workbench
                testId="transfers-workbench"
                title="Transfer picker"
                onClose={leaveWorkbench}
              >
                <TransfersPanel
                  /* Keyed on the scenario for the reason the side panel's copy gives. */
                  key={project.activeScenarioId}
                  state={liveDesign}
                  perspective={lens.perspective}
                  filters={lens.filters}
                  readOnly={readOnly}
                  scenarioId={project.activeScenarioId}
                  onDispatch={dispatch}
                  /*
                    Selecting a person is a canvas action, so it LEAVES: the person panel it opens is in
                    the side panel, which is behind this. Staying here would tick a name and show nothing.
                  */
                  onSelect={(employeeId) => {
                    leaveWorkbench();
                    selectPerson(employeeId);
                  }}
                  onOpenAssumptions={() => openModal('assumptions')}
                  onNext={() => {
                    leaveWorkbench();
                    choosePhase('tsa');
                  }}
                />
              </Workbench>
            ) : view === 'target' ? (
              <Workbench
                testId="target-workbench"
                title="Target org design"
                onClose={leaveWorkbench}
              >
                <TargetPanel
                  state={liveDesign}
                  readOnly={readOnly}
                  scenarioName={scenarioName ?? undefined}
                  onDispatch={dispatch}
                  onNext={() => {
                    leaveWorkbench();
                    choosePhase('perimeter');
                  }}
                />
              </Workbench>
            ) : view === 'entities' ? (
              <EntityPanel
                state={liveDesign}
                scenarioId={project.activeScenarioId}
                readOnly={readOnly}
                initialPerspective={lens.perspective}
                onDispatch={(commands) => void dispatch(commands)}
                /*
                  The matrix is the same question asked as a grid, so it REPLACES this rather than
                  stacking on it — which used to need a close and an open, and is now one move
                  between two places.
                */
                onOpenMatrix={() => {
                  setAnalyticsTab('coverage-matrix');
                  setView('analytics');
                }}
                onClose={leaveWorkbench}
              />
            ) : (
              <AnalyticsModal
                state={liveDesign}
                scenarioName={scenarioName ?? '—'}
                projectId={project.projectId}
                activeScenarioId={project.activeScenarioId}
                scenarioCount={project.scenarios.length}
                /* The level pane's opening perspective, so the tab shows what you were looking at. */
                chartPerspective={lens.perspective}
                /* The card palette: a bucket keeps one colour across the chart, the panes and the deck. */
                statusColors={cards.colors.status}
                tab={analyticsTab}
                onTab={setAnalyticsTab}
                /*
                  Leaves, and moves the PANE too.

                  `RemainCoPanel` renders only when `pane === 'phase'`, and Analytics is reachable from
                  the header whatever pane is open. So arriving from a person's card or a
                  multi-selection, this button left analytics, moved the phase spine to RemainCo,
                  flipped the chart to the remain perspective — and left the person panel on screen,
                  with the mitigation plan it names nowhere in sight.
                */
                onOpenPlan={() => {
                  setPhase('remainco');
                  setPane('phase');
                  leaveWorkbench();
                }}
                onOpenAssumptions={() => openModal('assumptions')}
                onClose={leaveWorkbench}
              />
            )}
          </ErrorBoundary>
        )}
        {liveDesign === null ? (
          <div className="chart-loading">Loading the design…</div>
        ) : (
          <OrgChart
            ref={chart}
            state={liveDesign}
            nodes={nodes}
            lens={chartLens}
            nodeDisplay={cards}
            rootLabel={project.name}
            selectedId={selectedId}
            selected={selection}
            selectMode={selectMode}
            members={peers}
            onSelect={(id) => {
              setSelectedId(id);
              select(id);
              setPane(id === null ? 'phase' : 'node');
            }}
            onToggleSelect={toggleSelect}
          />
        )}

        {/*
          A real <button>, not a div: it is focusable, it is announced, and the keyboard handler below
          gives it the same range the pointer has. A resize affordance nobody can reach without a
          mouse is the kind of control that passes review and fails an audit.
        */}
        <button
          className="panel-resize"
          data-testid="panel-resize"
          aria-label="Resize the panel"
          aria-valuenow={panelWidth}
          aria-valuemin={260}
          aria-valuemax={760}
          role="separator"
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPanelWidth((w) => Math.min(760, w + 16));
            else if (e.key === 'ArrowRight') setPanelWidth((w) => Math.max(260, w - 16));
            else return;
            e.preventDefault();
          }}
        />
        <aside className="side-panel" style={{ ['--panel-w' as string]: `${panelWidth}px` }}>
          {/*
            Panel scope, so a pane that cannot compute takes itself down and leaves the chart, the
            connection and everybody else's presence alive. A single root boundary would turn every
            one of these into a whole-app fault, which is the failure it exists to prevent wearing a
            better message.
          */}
          <ErrorBoundary scope="panel" label="side panel">
          {/*
            INSIDE the boundary, and ABOVE every section.

            Above, because `.side-panel section + section` supplies the panel's whole vertical rhythm
            and a control placed between two sections would be a sibling that breaks it. Inside,
            because a panel that has thrown replaces this too — a live "Collapse all sections" over an
            error banner with no sections under it is a control for nothing.
          */}
          <SectionToolbar />
          {/*
            "Changed by X while you were editing", beside the panel rather than in the global banner.

            The banner is for things about the session — an error, a reconnect. This is about the object
            in front of you, and it has to sit next to that object or it reads as an unrelated
            interruption. It is dismissible and it clears itself when you select something else, because
            a warning about the node you have left is noise on the one you have opened.

            It says what happened and who, and offers nothing else on purpose: the change is already
            applied and already in the log, so a button here would either duplicate the history modal's
            undo — which knows about the refusals this does not — or imply a "keep mine" that the
            server-authoritative model does not have.
          */}
          {conflict !== null && conflict.nodeId === selectedId && (
            <div className="banner notice" data-testid="conflict-notice">
              <span>
                <strong>{conflict.actorName}</strong> changed this while you had it open: “
                {conflict.summary}” (#{conflict.seq}).
              </span>
              <span className="spacer" />
              <button data-testid="conflict-dismiss" onClick={dismissConflict}>
                Dismiss
              </button>
            </div>
          )}
          {liveDesign && pane === 'selection' && rollup && (
            <SelectionPanel
              /* Reset with the scenario: a selection is decisions-in-progress about ONE design. */
              key={project.activeScenarioId}
              state={liveDesign}
              nodes={nodes}
              selection={selection}
              perspective={chartLens.perspective}
              /*
                The ROLE, not a derived flag. A reviewer may not write and is not silent: the same bulk
                becomes a suggestion an editor reviews as a diff, and the panel decides that with
                `bulkIntent`. Deriving it here instead left a seam where a literal `'apply'` passed every
                test — see the prop's own comment.
              */
              role={role ?? null}
              scenarioId={project.activeScenarioId}
              perimeterFrozen={perimeterFrozen}
              onRemove={(employeeId) =>
                setSelection((prev) => {
                  const next = new Set(prev);
                  next.delete(employeeId);
                  return next;
                })
              }
              onClear={() => {
                setSelection(new Set());
                setPane('phase');
              }}
              onDispatch={(command) => void dispatch(command)}
              onPropose={async (command, title) => {
                await api.createProposal(project.projectId, {
                  title,
                  commands: Array.isArray(command) ? [...command] : [command],
                  // The scenario the suggestion is ABOUT, so an editor applying it later cannot land it
                  // in whichever design they happen to have open.
                  scenarioId: project.activeScenarioId,
                });
              }}
              onSelectPerson={selectPerson}
            />
          )}
          {liveDesign && pane === 'phase' && phase === 'data' && (
            <DataPanel
              state={liveDesign}
              readOnly={readOnly}
              onDispatch={(command) => void dispatch(command)}
              onOpenDiagnostics={() => openModal('diagnostics')}
              onOpenMapping={() => openModal('mapping')}
              onOpenRefresh={() => openModal('refresh')}
              onOpenAssumptions={() => openModal('assumptions')}
              onNext={() => choosePhase(liveDesign.targetEnabled ? 'target' : 'perimeter')}
            />
          )}

          {liveDesign && pane === 'phase' && phase === 'perimeter' && (
            <PerimeterPanel
              selectMode={selectMode}
              onToggleSelectMode={() => setSelectMode((v) => !v)}
              state={liveDesign}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              perimeterFrozen={perimeterFrozen}
              canFreeze={role === 'owner'}
              onDispatch={dispatch}
              onNext={() => choosePhase('transfers')}
            />
          )}

          {/* Not while its own workbench is open — see the note on the target panel below. */}
          {liveDesign && pane === 'phase' && phase === 'transfers' && view !== 'transfers' && (
            <TransfersPanel
              /*
                Keyed on the scenario. `dispatch` refetches without unmounting, so a scenario switch
                would otherwise leave the picker's ticks in place and commit them against a design they
                were never chosen from.
              */
              key={project.activeScenarioId}
              state={liveDesign}
              perspective={lens.perspective}
              filters={lens.filters}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onDispatch={dispatch}
              onSelect={selectPerson}
              onOpenAssumptions={() => openModal('assumptions')}
              onNext={() => choosePhase('tsa')}
              onOpenFullWidth={() => setView('transfers')}
            />
          )}

          {liveDesign && pane === 'phase' && phase === 'tsa' && (
            <TsaPanel
              state={liveDesign}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onDispatch={dispatch}
              onOpenRegister={() => setView('tsa')}
              onOpenAssumptions={() => openModal('assumptions')}
              onNext={() => choosePhase('remainco')}
            />
          )}

          {liveDesign && pane === 'phase' && phase === 'remainco' && (
            <RemainCoPanel
              onOpenAnalytics={() => {
                setAnalyticsTab('stranded');
                setView('analytics');
              }}
              state={liveDesign}
              perspective={lens.perspective}
              filters={lens.filters}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onDispatch={dispatch}
              onOpenAssumptions={() => openModal('assumptions')}
            />
          )}

          {/*
            Not rendered while its own workbench is open. Hidden would not be enough: the side panel
            stays MOUNTED behind a workbench so the chart keeps its measurements, so a second copy of
            this component would put every one of its `data-testid`s in the document twice — and a
            duplicated testid is a broken selector in a 4,900-line suite, not a cosmetic problem.
          */}
          {liveDesign && pane === 'phase' && phase === 'target' && view !== 'target' && (
            <TargetPanel
              state={liveDesign}
              readOnly={readOnly}
              scenarioName={scenarioName ?? undefined}
              onDispatch={dispatch}
              onNext={() => choosePhase('perimeter')}
              onOpenFullWidth={() => setView('target')}
            />
          )}

          {liveDesign &&
            pane === 'phase' &&
            phase !== 'data' &&
            phase !== 'target' &&
            phase !== 'perimeter' &&
            phase !== 'transfers' &&
            phase !== 'tsa' &&
            phase !== 'remainco' && (
            <section data-testid="phase-panel-pending">
              <h2>{phaseById(phase).label}</h2>
              <p className="muted">
                This phase&rsquo;s panel is not on the platform yet — the chart, the lens and the
                decisions below it are. Select someone to work on them.
              </p>
            </section>
          )}

          {liveDesign && pane === 'node' && selectedEmployee && (
            <PersonPanel
              state={liveDesign}
              person={selectedEmployee}
              perspective={lens.perspective}
              phase={phase}
              decisions={decisions}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onSetBucket={setBucket}
              onDispatch={(command) => void dispatch(command)}
              onOpenAssumptions={() => openModal('assumptions')}
            />
          )}

          {liveDesign && pane === 'node' && !selectedEmployee && selectedGroup && (
            <GroupPanel
              state={liveDesign}
              node={selectedGroup}
              nodes={nodes}
              perspective={lens.perspective}
              targetView={targetActive}
              pivot={!targetActive && lens.viewGrouping.length > 0}
              decisions={decisions}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onDispatch={(command) => void dispatch(command)}
              /* The head's name is a way to them, the same gesture the selection panel's list uses. */
              onSelectPerson={selectPerson}
            />
          )}

          {/* The root card is selectable and is neither: it stands for the whole organisation. */}
          {pane === 'node' && selectedId !== null && !selectedEmployee && !selectedGroup && (
            <OrgPanel
              projectName={project.name}
              /* Null while the design loads — a restored `?node=` selection beats the fetch. */
              state={liveDesign}
              perspective={lens.perspective}
              targetView={targetActive}
              pivot={!targetActive && lens.viewGrouping.length > 0}
              readOnly={readOnly}
              scenarioId={project.activeScenarioId}
              onDispatch={(command) => void dispatch(command)}
              onSelectPerson={selectPerson}
            />
          )}

          {/* It had no testid, because nothing had ever needed to name it. Adding one is allowed. */}
          <Section
            testId="whos-here"
            /*
              THE COUNT IS IN THE HEADING, AND THAT IS WHAT MAKES FOLDING THIS SAFE.

              It folds by default because most of the time it says "Just you right now", which is the
              least useful line in the panel. But when colleagues ARE here it is the most live thing on
              screen, and a folded section that gave no sign of it would hide presence — a regression in
              the feature rather than a tidying of it. A pill survives the fold, so the section can be
              away and still say there are two other people in the project.
            */
            title={
              <>
                Who&apos;s here{' '}
                {peers.length > 0 && (
                  <span className="pill" data-testid="peers-pill">
                    {peers.length}
                  </span>
                )}
              </>
            }
          >
            {peers.length === 0 ? (
              <p className="muted">Just you right now.</p>
            ) : (
              <ul className="activity">
                {peers.map((p) => (
                  <li key={p.connectionId}>
                    <span className="who">{p.name}</span>
                    {p.selectedId && (
                      <span className="when" data-testid="peer-node">
                        {' · '}
                        {describeNodeId(p.selectedId, nameOfEmployee) ?? 'viewing a node'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/*
            Still a TICKER, deliberately, and it gains no undo button.

            Its rows come off the socket, and a socket frame carries neither `undone` nor the reason a
            change cannot be reverted — both are computed per read out of columns no frame has. It is also
            empty on page load, which is the one moment somebody most wants to take something back. So the
            affordance it gains is a way into the log, not a control of its own.
          */}
          <Section
            testId="activity"
            title="Activity"
            attrs={{ 'data-count': activity.length }}
          >
            {activity.length === 0 ? (
              <p className="muted">Changes from anyone on the project appear here.</p>
            ) : (
              <ul className="activity">
                {activity.map((e) => (
                  <li key={e.id} data-testid="activity-row" data-seq={e.seq}>
                    <div>{e.summary}</div>
                    <div className="when">
                      <span className="who">{e.actorName || 'Someone'}</span> ·{' '}
                      {new Date(e.createdAt).toLocaleTimeString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {project && (
              <button
                className="linkish"
                data-testid="activity-open-history"
                onClick={() => openModal('history')}
              >
                Full history, and undo
              </button>
            )}
          </Section>
          </ErrorBoundary>
        </aside>
      </div>

      {liveDesign && modal === 'diagnostics' && (
        <Diagnostics state={liveDesign} lastRun={lastReview} onClose={() => closeModal()} />
      )}
      {liveDesign && modal === 'export' && (
        <ExportModal
          projectId={project.projectId}
          state={liveDesign}
          scenarioId={project.activeScenarioId}
          scenarioName={scenarioName ?? 'this scenario'}
          scenarioCount={project.scenarios.length}
          canWrite={!readOnly}
          onClose={() => closeModal()}
        />
      )}
      {modal === 'history' && (
        <HistoryModal
          projectId={project.projectId}
          /*
            The names, so a row can say which design it changed. Passed rather than looked up inside,
            because a panel does not touch the store — and a change to a scenario that has since been
            deleted keeps its own row, with no name, rather than disappearing from the audit trail.
          */
          scenarios={project.scenarios}
          scenarioId={project.activeScenarioId}
          seq={seq}
          myUserId={myUserId}
          canWrite={!readOnly}
          /*
            Wider than `canWrite`, and asked separately for that reason. A reviewer changes nothing and
            still has something to say — the role exists for it — so the note composer is theirs while
            every Undo button on the same rows is not.
          */
          canComment={role ? canComment(role) : false}
          /*
            Stricter than `canWrite`, and asked separately for that reason: a restore replaces every
            scenario's design at once, so it is one of the owner-only commands rather than one an editor
            may issue. Offering it to an editor would be offering a button the server refuses.
          */
          isOwner={role === 'owner'}
          projectName={project.name}
          onRevert={revert}
          onNote={note}
          onRestore={restore}
          onClose={() => closeModal()}
        />
      )}
      {modal === 'proposals' && (
        <ProposalsModal
          projectId={project.projectId}
          state={liveDesign}
          /*
            `canWrite`, not `canComment`: accepting somebody else's batch IS making the change, and the
            events say so. A reviewer raises them and cannot accept them, which is the entire point of the
            role and the reason the route exists.
          */
          canApply={!readOnly}
          myUserId={myUserId}
          onClose={() => closeModal()}
        />
      )}
      {modal === 'members' && myUserId && (
        <MembersEditor
          projectId={project.projectId}
          /*
            Asked separately from `readOnly`, like the restore above and for the same reason: managing
            members is owner-only, so offering the controls to an editor would be offering buttons the
            server refuses. The list itself is not gated.
          */
          isOwner={role === 'owner'}
          /*
            The whole roster, which an owner always has: owners are never scoped. It feeds the scope
            editor's value lists — so nothing offered can match nobody — and its live matched count.
          */
          people={liveDesign?.employees ?? []}
          currentUserId={myUserId}
          onLeft={() => {
            closeModal();
            onClose();
            void loadProjects();
          }}
          onClose={() => closeModal()}
        />
      )}
      {modal === 'account' && myUserId && (
        <AccountModal
          currentUserId={myUserId}
          currentUserName={myUserName}
          isAdmin={isAdmin}
          onRenamed={applyOwnProfile}
          onClose={() => closeModal()}
        />
      )}
      {modal === 'project-settings' && (
        <ProjectSettings
          projectId={project.projectId}
          name={project.name}
          archived={project.archived}
          isOwner={role === 'owner'}
          /*
            The project LIST is refreshed after each of these, which no other dispatch has to do. Every
            other command changes the design, and the list carries none of it — but a rename and an
            archive both change a row that is already in hand, and `closeProject` does not refetch, so
            without this the reader lands back on a list still showing the old name.
          */
          onDispatch={async (command) => {
            const ok = await dispatch(command);
            if (ok) await loadProjects();
            return ok;
          }}
          /*
            Leaving is the whole of the aftermath. There is no project to re-render, the socket is
            about to be evicted by the server anyway, and staying on a screen whose every fetch now
            answers "not found" is the failure this route already fixed once for the OTHER member of
            a deleted project.
          */
          onDeleted={() => {
            closeModal();
            onClose();
            void loadProjects();
          }}
          onClose={() => closeModal()}
        />
      )}
      {project && modal === 'scenarios' && (
        <ScenariosModal
          scenarios={project.scenarios}
          activeScenarioId={project.activeScenarioId}
          readOnly={readOnly}
          isOwner={role === 'owner'}
          onDispatch={dispatch}
          onClose={() => closeModal()}
        />
      )}
      {project && modal === 'refresh' && (
        <RefreshModal
          projectId={project.projectId}
          isOwner={role === 'owner'}
          onDispatch={dispatch}
          onClose={() => closeModal()}
        />
      )}
      {liveDesign && modal === 'mapping' && (
        <MappingEditor
          state={liveDesign}
          projectName={project.name}
          readOnly={readOnly}
          /*
            Owner-only, like the command itself, and pointless without retained source rows — the
            handler refuses when nothing was kept. Offering a button whose only possible outcome is
            a refusal is the thing `assistantAvailable` exists to avoid elsewhere.
          */
          canRebuild={role === 'owner' && liveDesign.rows.length > 0}
          onApply={(commands) => void dispatch(commands)}
          onClose={() => closeModal()}
        />
      )}
      {liveDesign && modal === 'filters' && (
        <FiltersEditor
          state={liveDesign}
          filters={lens.filters}
          perspective={lens.perspective}
          onApply={(filters) => setLens({ filters })}
          onClose={() => closeModal()}
        />
      )}
      {liveDesign && modal === 'grouping' && (
        <GroupingEditor
          state={liveDesign}
          layout={lens.viewLayout}
          grouping={lens.viewGrouping}
          targetView={targetActive}
          onApply={(next) => setLens(next)}
          onClose={() => closeModal()}
        />
      )}
      {liveDesign && modal === 'display' && (
        <NodeDisplayEditor
          nodeDisplay={cards}
          pptxGeom={pptxGeom ?? freshPptxGeom()}
          readOnly={readOnly}
          /* The verdict travels: the editor holds an optimistic config and drops it on a refusal. */
          onDispatch={dispatch}
          onClose={() => closeModal()}
        />
      )}
      {liveDesign && modal === 'assumptions' && (
        <AssumptionsEditor
          state={liveDesign}
          readOnly={readOnly}
          onDispatch={(command) => void dispatch(command)}
          onClose={() => closeModal()}
        />
      )}

      {/*
        A SIBLING OF `.main`, NOT A CHILD, and not a portal either.

        Not a child, because `.workbench` is `position:absolute; inset:0` inside `.main` and would cover
        it — the assistant has to stay reachable while somebody reads a workbench, which is exactly when
        they are most likely to want to ask about what is in front of them.

        Not a portal, because `App.render.test.tsx` reads this client through `byId(container, …)` and a
        portalled subtree is not in that container. Rendering here costs nothing a portal would buy.

        PANEL SCOPE ON THE BOUNDARY, like the side panel: an assistant that cannot render takes itself
        down and leaves the chart, the connection and everybody else's presence alive. A root-scoped
        fault here would turn a conversation into a whole-app failure.
      */}
      <ErrorBoundary scope="panel" label="assistant">
        <AssistantDock
          projectId={project.projectId}
          /* The scenario the request is made IN — the model is never offered the field. */
          scenarioId={project.activeScenarioId}
          /* For naming people in the rendered changes, rather than showing ids. */
          state={liveDesign}
          role={role ?? null}
          assistantAvailable={assistantAvailable}
          /* The server's half of the microphone question; the browser answers the other half itself. */
          voiceAvailable={voiceAvailable}
          /* And the third, which is a separate Azure deployment and fails on its own. */
          realtimeAvailable={realtimeAvailable}
          /* So a proposal the reader raised offers Withdraw rather than an Apply the server refuses. */
          myUserId={myUserId}
          onOpenProposals={() => openModal('proposals')}
        />
      </ErrorBoundary>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function App(): JSX.Element {
  const user = useStore((s) => s.user);
  const bootstrapped = useStore((s) => s.bootstrapped);
  const project = useStore((s) => s.project);
  const bootstrap = useStore((s) => s.bootstrap);
  const openProject = useStore((s) => s.openProject);
  const closeProject = useStore((s) => s.closeProject);
  const logout = useStore((s) => s.logout);

  /*
    ONE BOOLEAN, not a second modal stack.

    `ProjectView` keeps a stack because its modals open each other — assumptions over analytics — and
    it is consumed by sixteen render branches that all need `liveDesign` and `project`. Lifting that
    machinery up here to share it would be a large refactor of the workspace so that one
    project-independent screen could join a queue it never needs to be in. The account modal is
    entered from two places and stacks with nothing, so it gets the smallest thing that works.
  */
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  /*
    THE ONE PLACE THE URL IS READ: on first load, and whenever the reader uses the back button.

    A link to `/p/p_abc/remainco?node=E42` has to survive arriving cold — no session state, nothing
    open — which is the whole point of it being a link. `openProject` is idempotent about the project
    already being open, so a `popstate` between two places in the SAME project only moves the phase
    and the selection, and does not tear the socket down and rebuild it.

    Guarded on `user`, because a link is exactly how somebody arrives signed out: the login form
    renders, and this runs the moment they are through it rather than dropping them on the project
    list having forgotten where they were going.
  */
  useEffect(() => {
    if (!user) return;

    /*
      IDEMPOTENT, which is not a nicety here.

      Calling `closeProject()` when nothing is open still fires a store update — it sets a dozen
      fields to null — and that re-render detached the project-list button out from under a click in
      the end-to-end run. Same on the other side: re-opening the project that is already open would
      tear down the socket and rebuild it on every `popstate` between two places inside one project.
      So this compares first and acts only on a real difference.
    */
    const apply = (): void => {
      const wanted = readPlace(window.location).projectId;
      const open = useStore.getState().project?.projectId ?? null;
      if (wanted === open) return;
      if (wanted) void openProject(wanted);
      else closeProject();
    };

    apply();
    window.addEventListener('popstate', apply);
    return () => window.removeEventListener('popstate', apply);
  }, [user, openProject, closeProject]);

  /*
    Opening and closing a project ARE navigations, so they push. Everything inside one replaces —
    see the note in `ProjectView`.

    AND IT MUST NOT FIRE WHILE A LINK IS STILL OPENING, which cost a diagnosis. The effect above
    calls `openProject` and returns; opening is a fetch, so `project` is still null when THIS effect
    runs in the same commit. Without the guard it saw "showing nothing, URL says p_abc", concluded
    the project had been closed, and pushed `/` — wiping the whole address, `?view=` and `?node=` and
    all, a few milliseconds before `ProjectView` mounted and read it.

    The symptom was that a deep link opened the chart, and every unit test in the client passed while
    it did. `settled` says whether this tab has ever finished arriving: until it has, a URL naming a
    project is an INTENT being carried out, not a stale value to correct.
  */
  const settled = useRef(false);
  useEffect(() => {
    if (!user) return;
    const wanted = project ? project.projectId : null;
    const shown = readPlace(window.location).projectId;
    if (!settled.current) {
      /*
        Still opening what the link named. Note this does NOT wait for success: an id that fails to
        open — deleted, or access withdrawn — settles on the next change and the bar is corrected
        then. Leaving a dead link in the address bar for a moment is a far smaller cost than
        destroying a live one on every cold load.
      */
      if (wanted === null && shown !== null) return;
      settled.current = true;
    }
    if (wanted !== shown) {
      syncUrl({ projectId: wanted, phase: null, node: null, tab: null, view: null }, 'push');
    }
  }, [user, project]);

  if (!bootstrapped) {
    return (
      <div className="centred">
        <Loading what="your projects" />
      </div>
    );
  }

  if (!user) return <Login />;
  if (project) return <ProjectView onClose={closeProject} />;

  return (
    <div className="app">
      <header className="header">
        <h1>Separation Studio</h1>
        <span className="spacer" />
        <span className="who">{user.name}</span>
        <button data-testid="open-account-shell" onClick={() => setAccountOpen(true)}>
          Account
        </button>
        <button onClick={() => void logout()}>Sign out</button>
      </header>
      <Banners />
      <div style={{ flex: 1, overflow: 'auto' }}>
        <ProjectList onOpen={(id) => void openProject(id)} />
      </div>
      {accountOpen && (
        <AccountModal
          currentUserId={user.id}
          currentUserName={user.name}
          isAdmin={user.isAdmin}
          onRenamed={(next) => useStore.getState().applyOwnProfile(next)}
          onClose={() => setAccountOpen(false)}
        />
      )}
    </div>
  );
}
